import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography, useTheme, Skeleton } from "@mui/material";
import { useGetAllScurveDataQuery } from "store/slices/apis/procurementSlice";
import { useParams } from "react-router-dom";
import { useMemo } from "react";

const SCurveProcurement = () => {
    const theme = useTheme();
    const { projectId } = useParams();

    const { data, isLoading, error } = useGetAllScurveDataQuery({ projectId });

    const allCategories = data?.map((item) => item.month) || [];

    const actual = useMemo(
        () => data?.map(d => (d?.actual === 0 ? null : d?.actual ?? null)) || [],
        [data]
    );
    const plan = useMemo(
        () => data?.map(d => (d?.plan === 0 ? null : d?.plan ?? null)) || [],
        [data]
    );
    const forecast = useMemo(
        () => data?.map(d => (d?.forecast === 0 ? null : d?.forecast ?? null)) || [],
        [data]
    );


    const isArrayAllZero = (array) => array.every((item) => item === 0);
    const isAllZero =
        isArrayAllZero(actual) &&
        isArrayAllZero(plan) &&
        isArrayAllZero(forecast);


    const categories = allCategories;

    const [columnData, setColumnData] = useState({
        actual: new Array(categories.length).fill(null),
        plan: new Array(categories.length).fill(null),
        forecast: new Array(categories.length).fill(null),
    });

    const options = {
        chart: {
            type: "spline",
            backgroundColor: "#fff",
            height: 330,
            spacingLeft: 0,
            spacingRight: 0,
            spacingTop: 0,
            spacingBottom: 0,
        },
        title: {
            text: "",
            align: "left",
            style: { fontSize: "24px", fontWeight: "bold", color: "#223365" },
        },
        xAxis: {
            categories,
            title: { text: 'Months' },
            lineWidth: 1,
            lineColor: '#758195',
            tickLength: 10,
            tickWidth: 1,
            tickColor: '#999',
            tickmarkPlacement: 'on',
        },
        yAxis: {
            title: { text: 'Progress Percentage' },
            gridLineWidth: 0,
            tickLength: 10,
            tickWidth: 1,
            tickColor: '#999',
            tickmarkPlacement: 'on',
            lineWidth: 1,
            lineColor: '#758195',
            tickPositioner: function () {
                let positions = [10];
                for (let i = 25; i <= 150; i += 25) {
                    positions.push(i);
                }
                return positions;
            },
        },
        tooltip: {
            shared: true,
            useHTML: true,
            formatter: function () {
                const planned = this.points.find(p => p.series.name === 'Planned')?.y ?? 0;
                const forecast = this.points.find(p => p.series.name === 'Forecast')?.y ?? 0;
                const actual = this.points.find(p => p.series.name === 'Actual')?.y ?? 0;

                return `
        <b>Planned: ${planned}</b><br/>
        <b>Forecast: ${forecast}</b><br/>
        <b>Actual: ${actual}</b>
    `;
            }

        },
        legend: {
            align: "left",
            verticalAlign: "top",
            layout: "horizontal",
            itemDistance: 20,
            useHTML: true,
            symbolWidth: 0,
            x: -10,
            itemMarginBottom: 20,
            labelFormatter: function () {
                return `
        <span style="display: inline-block; width: 11px; height: 11px; border-radius: 0%; background-color: ${this.color}; margin-right: 5px;"></span>
        <span style="font-weight: 500; font-size: 15px; letter-spacing: -0.4px; color:#182A4E">${this.name}</span>
    `;
            }

        },
        plotOptions: {
            series: {
                point: {
                    // events: {
                    //     mouseOver: function () {
                    //         const index = this.index;
                    //         setColumnData({
                    //             actual: actual.map((val, i) => (i === index ? val : null)),
                    //             plan: plan.map((val, i) => (i === index ? val : null)),
                    //             forecast: forecast.map((val, i) => (i === index ? val : null)),
                    //         });
                    //     },
                    //     mouseOut: function () {
                    //         setColumnData({
                    //             actual: new Array(categories.length).fill(null),
                    //             plan: new Array(categories.length).fill(null),
                    //             forecast: new Array(categories.length).fill(null),
                    //         });
                    //     },
                    // },
                },
            },
        },
        series: [
            {
                name: "Planned",
                data: plan,
                color: "#295477",
                lineWidth: 3,
                marker: { enabled: true, symbol: "circle" },
            },
            {
                name: "Actual",
                data: actual,
                color: "#E72B33",
                marker: { enabled: true },
                lineWidth: 3,
            },
            {
                name: "Forecast",
                data: forecast,
                color: "#FFBF00",
                marker: { enabled: true },
                lineWidth: 3,
            },
            // {
            //     type: "column",
            //     name: "Actual Column",
            //     data: columnData.actual,
            //     color: "#252525",
            //     borderWidth: 0,
            //     enableMouseTracking: true,
            //     showInLegend: false,
            // },
            // {
            //     type: "column",
            //     name: "Plan Column",
            //     data: columnData.plan,
            //     color: "#415385",
            //     borderWidth: 0,
            //     enableMouseTracking: true,
            //     showInLegend: false,
            // },
            // {
            //     type: "column",
            //     name: "Forecast Column",
            //     data: columnData.forecast,
            //     color: "#C84757",
            //     borderWidth: 0,
            //     enableMouseTracking: true,
            //     showInLegend: false,
            // },
        ],
        credits: {
            enabled: false,
        },
    };

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                mt: 0,
                borderRadius: "7px",
                overflow: "hidden",
                backgroundColor: "white",
                border: "1px solid #2525252E",
                p: 2
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: "500",
                    fontSize: '18px',
                    color: `${theme?.customSettings?.themeColor}`,
                    lineHeight: '100%',
                    letterSpacing: '-0.4px',

                }}
            >
                Procurement Progress - S Curve
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    fontWeight: "400",
                    fontSize: '14px',
                    color: '#182A4E80',
                    lineHeight: '100%',
                    letterSpacing: '-0.4px',
                }}
            >
            </Typography>
            {isLoading ? (
                <Skeleton variant="rectangular" height={320} width="100%" />
            ) : error ? (
                <Typography color="error" sx={{ textAlign: "center", mt: 5 }}>
                    Error loading data
                </Typography>
            ) : isAllZero ? (
                <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                        No data available to display the chart.
                    </Typography>
                </Box>
            )
                : (

                    <Box mt="36px">
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    </Box>

                )}

        </Box>
    );
};

export default SCurveProcurement;
